import {useContext, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router";
import {useTranslation} from "react-i18next";
import {
    faArrowLeft,
    faCartPlus,
    faCheck,
    faChevronLeft,
    faChevronRight,
    faCircleInfo,
    faEnvelope,
    faFireFlameCurved,
    faShoppingCart,
    faWaveform
} from "@fortawesome/pro-solid-svg-icons";

import {Styled} from "./Productpage.styles";
import ActionButton from "../../lib/UIBookBrd/ActionButton";
import Stepper from "../../lib/UIBookBrd/Stepper";
import ProductCard from "../RentPageList/ProductCard";
import useS115 from "../../lib/Hooks/useS115";
import useBasket from "../../lib/Hooks/useBasket";
import useLanguage from "../../lib/Hooks/useLanguage";
import {Local_Data} from "../../Context";
import {Contact__Values, descriptions} from "../../PageValues";
import {RENT_LIST_PAGE, RENT_PAGE} from "../../Routes";
import {
    filterByCategory,
    getProductIcon,
    getProductName,
    hasNumericPrice,
    SLUG_BY_CATEGORY,
    toProductList
} from "../../lib/products";

const SIMILAR_COUNT = 4

const ProductPage = () => {

    const {productName} = useParams();

    const {t} = useTranslation()

    const {navigatePage} = useS115()

    const {language} = useLanguage()

    const [data, setLocalData] = useContext<any>(Local_Data)

    const {getCount, addItem, removeItem} = useBasket()

    const [imageIndex, setImageIndex] = useState<number>(0)

    const {Products, isLoadingProducts} = data ?? {}

    const productId = productName ?? ''

    const value = Products?.[productId]

    const similar = useMemo(() => {
        const category = value?.category?.[0]
        return filterByCategory(toProductList(Products), category)
            .filter(({id}) => id !== productId)
            .slice(0, SIMILAR_COUNT)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Products, productId, value?.category])

    useEffect(() => {
        setImageIndex(0)
    }, [productId]);

    if (!value) {
        return (
            <Styled.Page>
                <Styled.NotFound>
                    <Styled.NotFoundIcon icon={faWaveform}/>
                    <Styled.NotFoundTitle>
                        {isLoadingProducts ? t('lbl_loading') : t('lbl_product_not_found')}
                    </Styled.NotFoundTitle>
                    <Styled.NotFoundText>
                        {isLoadingProducts ? t('lbl_loading_desc') : t('lbl_product_not_found_desc')}
                    </Styled.NotFoundText>
                    <ActionButton
                        variant={'outline'}
                        icon={faArrowLeft}
                        label={t('lbl_back_to_offer')}
                        onClick={() => navigatePage(RENT_LIST_PAGE)}
                    />
                </Styled.NotFound>
            </Styled.Page>
        )
    }

    const {
        producent,
        count = 0,
        rentPrice,
        type,
        isSpecialPrice,
        image,
        imageGallery
    } = value

    const images: string[] = (imageGallery?.length ? imageGallery : [image]).filter(Boolean)

    const inBasket = getCount(productId)

    const specs: string[] = descriptions[productId]?.[language] ?? []

    const descriptionKey = `lbl_${productId}_desc`

    const description = t(descriptionKey)

    const hasDescription = description !== descriptionKey

    const categorySlug = value?.category?.[0] ? SLUG_BY_CATEGORY[value.category[0]] : undefined

    const openBasket = () => setLocalData((state: any) => ({...state, isBasketOpen: true}))

    const stepImage = (direction: number) => {
        setImageIndex((previous) => (previous + direction + images.length) % images.length)
    }

    return (
        <Styled.Page>
            <Styled.TopBar>
                <Styled.BackButton
                    onClick={() => navigatePage(categorySlug ? `${RENT_PAGE}/${categorySlug}` : RENT_LIST_PAGE)}
                >
                    <Styled.BackIcon icon={faArrowLeft}/>
                    {t('lbl_back_to_offer')}
                </Styled.BackButton>
                <Styled.Breadcrumb>
                    {`${t('lbl_rent_equipment')} — ${getProductName(productId, value, t)}`}
                </Styled.Breadcrumb>
            </Styled.TopBar>

            <Styled.Layout>
                <Styled.Gallery>
                    <Styled.Frame>
                        <Styled.FrameImage
                            src={images[imageIndex]}
                            alt={getProductName(productId, value, t)}
                            fit={'contain'}
                            icon={getProductIcon(type)}
                        />
                        {images.length > 1 && (
                            <>
                                <Styled.FrameNav
                                    $side={'left'}
                                    onClick={() => stepImage(-1)}
                                    aria-label={t('lbl_previous')}
                                >
                                    <Styled.FrameNavIcon icon={faChevronLeft}/>
                                </Styled.FrameNav>
                                <Styled.FrameNav
                                    $side={'right'}
                                    onClick={() => stepImage(1)}
                                    aria-label={t('lbl_next')}
                                >
                                    <Styled.FrameNavIcon icon={faChevronRight}/>
                                </Styled.FrameNav>
                                <Styled.FrameCounter>
                                    {`${imageIndex + 1} / ${images.length}`}
                                </Styled.FrameCounter>
                            </>
                        )}
                    </Styled.Frame>

                    {images.length > 1 && (
                        <Styled.Thumbs>
                            {images.map((source, index) => (
                                <Styled.Thumb
                                    key={source}
                                    $active={index === imageIndex}
                                    onClick={() => setImageIndex(index)}
                                >
                                    <Styled.ThumbImage
                                        src={source}
                                        alt={''}
                                        fit={'contain'}
                                        icon={getProductIcon(type)}
                                    />
                                </Styled.Thumb>
                            ))}
                        </Styled.Thumbs>
                    )}
                </Styled.Gallery>

                <Styled.Panel>
                    <Styled.Producer>{producent ?? 'S115'}</Styled.Producer>
                    <Styled.Title>{getProductName(productId, value, t)}</Styled.Title>

                    <Styled.Chips>
                        {type && (
                            <Styled.Chip>
                                <Styled.ChipIcon icon={getProductIcon(type)}/>
                                {type}
                            </Styled.Chip>
                        )}
                        <Styled.Chip>
                            {`${t('lbl_in_stock')}: ${count} ${t('lbl_pieces_short')}`}
                        </Styled.Chip>
                        {isSpecialPrice && (
                            <Styled.Chip $tone={'accent'}>
                                <Styled.ChipIcon icon={faFireFlameCurved}/>
                                {t('lbl_hot_price')}
                            </Styled.Chip>
                        )}
                    </Styled.Chips>

                    <Styled.PriceRow>
                        {hasNumericPrice(rentPrice) ? (
                            <>
                                <Styled.Price>{`${rentPrice} PLN`}</Styled.Price>
                                <Styled.PriceUnit>{t('lbl_rent_day')}</Styled.PriceUnit>
                            </>
                        ) : (
                            <Styled.Price>{t(rentPrice as string)}</Styled.Price>
                        )}
                    </Styled.PriceRow>

                    {hasDescription && (
                        <Styled.Description>{description}</Styled.Description>
                    )}

                    <Styled.Actions>
                        {inBasket > 0 ? (
                            <Styled.ActionRow>
                                <Stepper
                                    value={inBasket}
                                    max={count}
                                    unit={t('lbl_pieces_short')}
                                    removeAtMin={true}
                                    decreaseLabel={t('lbl_remove')}
                                    increaseLabel={t('lbl_add_to_basket')}
                                    onDecrease={() => removeItem(productId)}
                                    onIncrease={() => addItem(productId, count)}
                                />
                                <ActionButton
                                    fullWidth={true}
                                    icon={faShoppingCart}
                                    label={t('lbl_go_to_basket')}
                                    onClick={openBasket}
                                />
                            </Styled.ActionRow>
                        ) : (
                            <ActionButton
                                fullWidth={true}
                                size={'lg'}
                                icon={faCartPlus}
                                label={t('lbl_add_to_basket')}
                                onClick={() => addItem(productId, count)}
                            />
                        )}
                        <ActionButton
                            fullWidth={true}
                            variant={'outline'}
                            icon={faEnvelope}
                            label={t('lbl_ask_about_date')}
                            onClick={() => {
                                window.location.href = `mailto:${Contact__Values.emailAddress}?subject=${encodeURIComponent(`${t('lbl_ask_about_date')} — ${getProductName(productId, value, t)}`)}`
                            }}
                        />
                    </Styled.Actions>

                    <Styled.Note>
                        <Styled.NoteIcon icon={faCircleInfo}/>
                        {t('lbl_price_info')}
                    </Styled.Note>
                </Styled.Panel>
            </Styled.Layout>

            {!!specs.length && (
                <Styled.Section>
                    <Styled.SectionHead>
                        <Styled.SectionTitle>{t('lbl_long_product_description')}</Styled.SectionTitle>
                    </Styled.SectionHead>
                    <Styled.SpecList>
                        {specs.map((spec) => (
                            <Styled.SpecItem key={spec}>
                                <Styled.SpecIcon icon={faCheck}/>
                                {spec}
                            </Styled.SpecItem>
                        ))}
                    </Styled.SpecList>
                </Styled.Section>
            )}

            {!!similar.length && (
                <Styled.Section>
                    <Styled.SectionHead>
                        <Styled.SectionTitle>{t('lbl_similar_gear')}</Styled.SectionTitle>
                    </Styled.SectionHead>
                    <Styled.SimilarGrid>
                        {similar.map(({id, value: similarValue}, index) => (
                            <ProductCard
                                key={id}
                                id={id}
                                value={similarValue}
                                index={index}
                            />
                        ))}
                    </Styled.SimilarGrid>
                </Styled.Section>
            )}
        </Styled.Page>
    )
}

export default ProductPage
